import { ElementType, Element as IElement } from '@ritabygglov/core';
import { CompositeCommand } from '../commands/CompositeCommand';
import { CreateCommand } from '../commands/CreateCommand';
import { DeleteCommand } from '../commands/DeleteCommand';
import { UpdateCommand } from '../commands/UpdateCommand';
import { useAppState } from '../context/AppState/useAppState';
import {
  canGroupSelection,
  cloneElementsWithNewIds,
  expandGroups,
  mergeSelectionIntoGroup,
  resolveUngroupTarget,
} from '../helpers/groupHelpers';
import { copy } from '../helpers/utility';
import { SelectionModes } from '../types';

export const useElementActions = () => {
  const {
    selectedElements,
    setSelectedElements,
    selectionMode,
    setSelectionMode,
    commandManager,
    appState,
    session,
  } = useAppState();

  // re-order the elements by adding or removing from the renderingOrder number,
  // in case of equal elements, the tie-braker is the id of the element.
  // bring to top brings it to the new highest renderingOrder
  // bring to bottom brings it to the new lowest renderingOrder
  // at some point this needs to be stored as an operation

  const getSelected = () =>
    selectedElements
      .map((id) => appState.elements[id])
      .filter((el): el is IElement => el !== undefined);

  const getMovedElements = () => expandGroups(getSelected(), appState.elements);

  const changeRenderingOrder = (change: number) => {
    const elements = getMovedElements();
    if (elements.length === 0 || change === 0) return;
    commandManager.executeCommand(
      new UpdateCommand(
        elements.map((element) => ({
          ...copy(element),
          renderingOrder: element.renderingOrder + change,
        })),
        copy(elements),
        session.currentUserId,
        false
      )
    );
  };

  const canGroup = canGroupSelection(selectedElements, selectionMode);

  // Merging a selection replaces any groups it contained with one new group
  // over the flattened children — v1 has no nested groups.
  const onGroup = () => {
    if (!canGroup) return;
    const { newGroup, mergedGroups } = mergeSelectionIntoGroup(
      selectedElements,
      appState.elements,
      session.currentUserId,
      Object.keys(appState.elements).length + 1
    );
    const command = new CompositeCommand([
      new CreateCommand([newGroup], session.currentUserId),
      new DeleteCommand(mergedGroups, session.currentUserId),
    ]);
    commandManager.executeCommand(command);
    setSelectedElements([newGroup.id]);
    setSelectionMode({
      type: SelectionModes.Selected,
      elementType: ElementType.Group,
    });
  };

  const ungroupTarget = resolveUngroupTarget(
    selectedElements,
    appState.elements,
    selectionMode
  );

  // Deleting the Group record dissolves the group; its children are plain
  // elements again and stay selected so they can be re-grouped or moved.
  const onUngroup = () => {
    if (!ungroupTarget) return;
    const command = new DeleteCommand([ungroupTarget], session?.currentUserId);
    commandManager.executeCommand(command);
    setSelectedElements(ungroupTarget.childIds);
    setSelectionMode({
      type: SelectionModes.Selected,
      elementType: undefined,
    });
  };

  const onDelete = () => {
    const elements = getMovedElements();
    if (elements.length === 0) return;
    commandManager.executeCommand(
      new DeleteCommand(copy(elements), session?.currentUserId)
    );
  };

  const getOtherRenderingOrders = () => {
    const movedIds = new Set(getMovedElements().map((el) => el.id));
    return Object.values(appState.elements)
      .filter((el) => !movedIds.has(el.id))
      .map((el) => el.renderingOrder);
  };

  const getMovedRenderingOrders = () =>
    getMovedElements().map((el) => el.renderingOrder);

  const getToTop = () => {
    const others = getOtherRenderingOrders();
    if (others.length === 0) return 0;
    const diff = Math.max(...others) - Math.min(...getMovedRenderingOrders());
    if (diff < 0) {
      // The element is already at the top
      return 0;
    }
    return diff + 1;
  };

  const getToBottom = () => {
    const others = getOtherRenderingOrders();
    if (others.length === 0) return 0;
    const diff = Math.max(...getMovedRenderingOrders()) - Math.min(...others);
    if (diff < 0) {
      // already at the bottom
      return 0;
    }
    return diff + 1;
  };

  // TODO: If something is either at the top or bottom. Keep them at the same position.
  const onBringUp = () => changeRenderingOrder(+1);
  const onBringDown = () => changeRenderingOrder(-1);

  const onBringToTop = () => changeRenderingOrder(getToTop());
  const onBringToBottom = () => changeRenderingOrder(-getToBottom());

  const onDuplicate = () => {
    const elements = getMovedElements();
    const { elements: newElements, idMap } = cloneElementsWithNewIds(
      elements,
      50,
      50,
      Object.keys(appState.elements).length + 1
    );
    if (newElements.length === 0) return;

    commandManager.executeCommand(
      new CreateCommand(newElements, session?.currentUserId)
    );
    const createdIds = new Set(newElements.map((el) => el.id));
    setSelectedElements(
      selectedElements
        .map((id) => idMap.get(id))
        .filter((id): id is string => id !== undefined && createdIds.has(id))
    );
  };

  return {
    onBringUp,
    onBringDown,
    onBringToBottom,
    onBringToTop,
    onDelete,
    onDuplicate,
    onUngroup,
    onGroup,
  };
};
